import { useVirtualizer } from '@tanstack/react-virtual';

interface UseReactVirtualParams {
  containerElement: HTMLElement | null;
  items: unknown[];
  rowHeight?: number;
  overscan?: number;
}

interface UseReactVirtualReturn {
  beforeHeight: number;
  afterHeight: number;
  virtualItems: unknown[];
}

/**
 * Hook that provides virtualization for large lists
 * @param params Object containing container element, items, row height, and overscan
 * @returns Object containing virtualization data
 */
export const useReactVirtual = ({
  containerElement,
  items,
  rowHeight = 48,
  overscan = 10,
}: UseReactVirtualParams): UseReactVirtualReturn => {
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => containerElement,
    estimateSize: () => rowHeight,
    overscan,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const [beforeHeight, afterHeight] =
    virtualItems.length > 0
      ? [
          virtualItems[0].start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end,
        ]
      : [0, 0];

  return {
    beforeHeight,
    afterHeight,
    virtualItems,
  };
};
